import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { UpcomingRenewalsCard } from "../components/UpcomingRenewalsCard";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Plus, Server, Mail as MailIcon, ShieldCheck, Globe,
  Search, ChevronDown, ChevronRight, Edit3, Trash2, Bell, X, Receipt,
} from "lucide-react";

const SERVICES = [
  { key: "hosting", label: "Hosting", icon: Globe, enabled: true },
  { key: "email", label: "Email", icon: MailIcon, enabled: true },
  { key: "ssl", label: "SSL", icon: ShieldCheck, enabled: true },
  { key: "server", label: "Server", icon: Server, enabled: true },
];

const DURATIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "yearly", label: "Yearly" },
];

const EMAIL_TYPES = [
  { value: "google_workspace", label: "Google Workspace" },
  { value: "business_email", label: "Business Email" },
];

const YEAR_OPTIONS = [1, 2, 3, 4, 5];

const SERVER_TYPES = [
  { value: "vps", label: "VPS" },
  { value: "dedicated", label: "Dedicated" },
];

const SERVER_STATUSES = [
  { value: "active", label: "Active" },
  { value: "terminated", label: "Terminated" },
];

const SERVER_VENDORS = [
  { value: "leapswitch", label: "LeapSwitch" },
  { value: "aws", label: "AWS" },
  { value: "webyne", label: "Webyne" },
  { value: "others", label: "Others" },
];

const emptyForm = {
  service_type: "hosting",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  domain: "",
  last_invoice_number: "",
  last_invoice_date: "",
  duration: "yearly",
  notes: "",
  email_type: "google_workspace",
  total_users: "",
  pending_users: "",
  ssl_issuer: "",
  ssl_validity_years: "1",
  ssl_renewed_for_years: "",
  server_type: "vps",
  server_old_ip: "",
  server_new_ip: "",
  server_hostname: "",
  server_config: "",
  server_status: "active",
  server_vendor: "leapswitch",
};

function toDMY(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch { return ""; }
}

function dmyToISO(dmy) {
  if (!dmy) return null;
  const m = String(dmy).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mmv, yyyy] = m;
  const d = new Date(`${yyyy}-${mmv}-${dd}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function isValidDMY(dmy) {
  if (!dmy) return true; // empty allowed
  return /^\d{2}\/\d{2}\/\d{4}$/.test(String(dmy).trim()) && dmyToISO(dmy) !== null;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch { return "—"; }
}

function RenewalBadge({ days }) {
  if (days == null) return <span className="beacon-badge bg-slate-100 text-slate-500 border-slate-200">no date</span>;
  if (days < 0) return <span className="beacon-badge bg-rose-50 text-rose-700 border-rose-200"><span className="beacon-dot bg-rose-600" /> {Math.abs(days)}d overdue</span>;
  if (days <= 7) return <span className="beacon-badge bg-amber-50 text-amber-800 border-amber-200"><span className="beacon-dot bg-amber-500" /> {days}d left</span>;
  if (days <= 30) return <span className="beacon-badge bg-blue-50 text-blue-700 border-blue-200"><span className="beacon-dot bg-blue-500" /> {days}d left</span>;
  return <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200"><span className="beacon-dot bg-emerald-500" /> {days}d left</span>;
}

function SubscriptionModal({ open, initial, defaultService, customers = [], onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestQuery, setSuggestQuery] = useState("");
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          service_type: initial.service_type || "hosting",
          customer_name: initial.customer_name || "",
          customer_email: initial.customer_email || "",
          customer_phone: initial.customer_phone || "",
          domain: initial.domain || "",
          last_invoice_number: initial.last_invoice_number || "",
          last_invoice_date: toDMY(initial.last_invoice_date),
          duration: initial.duration || "yearly",
          notes: initial.notes || "",
          email_type: initial.email_type || "google_workspace",
          total_users: initial.total_users != null ? String(initial.total_users) : "",
          pending_users: initial.pending_users != null ? String(initial.pending_users) : "",
          ssl_issuer: initial.ssl_issuer || "",
          ssl_validity_years: initial.ssl_validity_years != null ? String(initial.ssl_validity_years) : "1",
          ssl_renewed_for_years: initial.ssl_renewed_for_years != null ? String(initial.ssl_renewed_for_years) : "",
          server_type: initial.server_type || "vps",
          server_old_ip: initial.server_old_ip || "",
          server_new_ip: initial.server_new_ip || "",
          server_hostname: initial.server_hostname || "",
          server_config: initial.server_config || "",
          server_status: initial.server_status || "active",
          server_vendor: initial.server_vendor || "leapswitch",
        });
      } else {
        setForm({ ...emptyForm, service_type: defaultService || "hosting" });
      }
    }
  }, [open, initial, defaultService]);

  if (!open) return null;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEmail = form.service_type === "email";
  const isSsl = form.service_type === "ssl";
  const isServer = form.service_type === "server";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.last_invoice_date) {
        const iso = dmyToISO(payload.last_invoice_date);
        if (!iso) {
          toast.error("Invalid date", { description: "Use dd/mm/yyyy format for Last invoice date." });
          setSaving(false);
          return;
        }
        payload.last_invoice_date = iso;
      } else {
        payload.last_invoice_date = null;
      }
      if (payload.service_type === "email") {
        payload.total_users = payload.total_users === "" ? null : Number(payload.total_users);
        payload.pending_users = payload.pending_users === "" ? null : Number(payload.pending_users);
        delete payload.ssl_issuer; delete payload.ssl_validity_years; delete payload.ssl_renewed_for_years;
        delete payload.server_type; delete payload.server_old_ip; delete payload.server_new_ip;
        delete payload.server_hostname; delete payload.server_config; delete payload.server_status; delete payload.server_vendor;
      } else if (payload.service_type === "ssl") {
        payload.ssl_validity_years = payload.ssl_validity_years === "" ? null : Number(payload.ssl_validity_years);
        payload.ssl_renewed_for_years = payload.ssl_renewed_for_years === "" ? null : Number(payload.ssl_renewed_for_years);
        delete payload.duration;
        delete payload.email_type; delete payload.total_users; delete payload.pending_users;
        delete payload.server_type; delete payload.server_old_ip; delete payload.server_new_ip;
        delete payload.server_hostname; delete payload.server_config; delete payload.server_status; delete payload.server_vendor;
      } else if (payload.service_type === "server") {
        payload.domain = ""; // Server uses hostname instead of domain
        delete payload.email_type; delete payload.total_users; delete payload.pending_users;
        delete payload.ssl_issuer; delete payload.ssl_validity_years; delete payload.ssl_renewed_for_years;
      } else {
        delete payload.email_type; delete payload.total_users; delete payload.pending_users;
        delete payload.ssl_issuer; delete payload.ssl_validity_years; delete payload.ssl_renewed_for_years;
        delete payload.server_type; delete payload.server_old_ip; delete payload.server_new_ip;
        delete payload.server_hostname; delete payload.server_config; delete payload.server_status; delete payload.server_vendor;
      }
      if (isEdit) {
        await api.patch(`/subscriptions/${initial.id}`, payload);
        toast.success("Subscription updated");
      } else {
        await api.post("/subscriptions", payload);
        toast.success("Subscription added");
      }
      onSaved();
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="subscription-modal"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-slate-900">
            {isEdit ? "Edit subscription" : "Add subscription"}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="beacon-label">Service</label>
              <select className="beacon-input" data-testid="sub-service-select"
                value={form.service_type} onChange={upd("service_type")}>
                {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            {!isSsl && (
              <div>
                <label className="beacon-label">Billing cycle</label>
                <select className="beacon-input" data-testid="sub-duration-select"
                  value={form.duration} onChange={upd("duration")}>
                  {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            )}
          </div>
          {!isEdit && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 -mb-1">
              <label className="beacon-label flex items-center gap-2">
                <span>Existing customer?</span>
                <span className="text-[10px] font-mono text-slate-400">{customers.length} on file</span>
              </label>
              <div className="relative">
                <input
                  className="beacon-input"
                  placeholder="Type a name, email or phone to auto-fill…"
                  value={suggestQuery}
                  data-testid="sub-customer-autocomplete"
                  onChange={(e) => { setSuggestQuery(e.target.value); setSuggestOpen(true); }}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                />
                {suggestOpen && suggestQuery.trim().length >= 1 && (() => {
                  const s = suggestQuery.trim().toLowerCase();
                  const hits = customers.filter((c) =>
                    (c.customer_name || "").toLowerCase().includes(s) ||
                    (c.customer_email || "").toLowerCase().includes(s) ||
                    (c.customer_phone || "").toLowerCase().includes(s)
                  ).slice(0, 8);
                  return (
                    <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-white rounded-lg border border-slate-200 shadow-lg max-h-72 overflow-y-auto" data-testid="sub-customer-suggestions">
                      {hits.length === 0 ? (
                        <div className="p-3 text-xs text-slate-500 text-center">No existing customer matches — this will be added as new.</div>
                      ) : (
                        hits.map((c) => (
                          <button
                            key={c.customer_email}
                            type="button"
                            data-testid={`sub-customer-suggest-${c.customer_email}`}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                            onClick={() => {
                              setForm((f) => ({ ...f,
                                customer_name: c.customer_name || "",
                                customer_email: c.customer_email || "",
                                customer_phone: c.customer_phone || "",
                              }));
                              setSuggestQuery("");
                              setSuggestOpen(false);
                            }}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-slate-900 truncate">{c.customer_name}</div>
                              <div className="text-[11px] text-slate-500 truncate font-mono">{c.customer_email}{c.customer_phone ? ` · ${c.customer_phone}` : ""}</div>
                            </div>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 shrink-0">{c.count} svc</span>
                          </button>
                        ))
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="beacon-label">Customer name</label>
              <input required className="beacon-input" data-testid="sub-name-input"
                value={form.customer_name} onChange={upd("customer_name")} />
            </div>
            <div>
              <label className="beacon-label flex items-center gap-2">
                <span>Customer email</span>
                {(() => {
                  const em = (form.customer_email || "").toLowerCase().trim();
                  const match = em && customers.find((c) => (c.customer_email || "").toLowerCase() === em);
                  if (!match) return null;
                  const isSameAsInitial = isEdit && (initial?.customer_email || "").toLowerCase() === em;
                  if (isSameAsInitial) return null;
                  return (
                    <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="sub-email-existing-badge">
                      existing · {match.count} service{match.count === 1 ? "" : "s"}
                    </span>
                  );
                })()}
              </label>
              <input required type="email" className="beacon-input" data-testid="sub-email-input"
                value={form.customer_email} onChange={upd("customer_email")} />
            </div>
          </div>
          <div className={`grid ${isServer ? "sm:grid-cols-1" : "sm:grid-cols-2"} gap-4`}>
            <div>
              <label className="beacon-label">Phone number</label>
              <input className="beacon-input" data-testid="sub-phone-input"
                value={form.customer_phone} onChange={upd("customer_phone")} />
            </div>
            {!isServer && (
              <div>
                <label className="beacon-label">Domain</label>
                <input className="beacon-input" placeholder="example.com" data-testid="sub-domain-input"
                  value={form.domain} onChange={upd("domain")} />
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="beacon-label">Last invoice #</label>
              <input className="beacon-input font-mono" data-testid="sub-invoice-no-input"
                value={form.last_invoice_number} onChange={upd("last_invoice_number")} />
            </div>
            <div>
              <label className="beacon-label">Last invoice date</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="dd/mm/yyyy"
                pattern="\d{2}/\d{2}/\d{4}"
                maxLength={10}
                className={`beacon-input font-mono ${form.last_invoice_date && !isValidDMY(form.last_invoice_date) ? "border-rose-400 focus:border-rose-500" : ""}`}
                data-testid="sub-invoice-date-input"
                value={form.last_invoice_date}
                onChange={(e) => {
                  // Auto-insert slashes as user types digits (dd/mm/yyyy)
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                  let out = digits;
                  if (digits.length > 4) out = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
                  else if (digits.length > 2) out = `${digits.slice(0,2)}/${digits.slice(2)}`;
                  setForm({ ...form, last_invoice_date: out });
                }}
              />
              {form.last_invoice_date && !isValidDMY(form.last_invoice_date) && (
                <div className="text-[11px] text-rose-600 mt-1">Enter a valid date as dd/mm/yyyy.</div>
              )}
            </div>
          </div>
          {isEmail && (
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-4" data-testid="email-fields-block">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-blue-700">
                <MailIcon className="w-3.5 h-3.5" /> Email plan details
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="beacon-label">Type</label>
                  <select className="beacon-input" data-testid="sub-email-type-select"
                    value={form.email_type} onChange={upd("email_type")}>
                    {EMAIL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="beacon-label">Total users</label>
                  <input type="number" min="0" className="beacon-input font-mono" data-testid="sub-total-users-input"
                    value={form.total_users} onChange={upd("total_users")} />
                </div>
                <div>
                  <label className="beacon-label">Pending users</label>
                  <input type="number" min="0" className="beacon-input font-mono" data-testid="sub-pending-users-input"
                    value={form.pending_users} onChange={upd("pending_users")} />
                </div>
              </div>
            </div>
          )}
          {isSsl && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 space-y-4" data-testid="ssl-fields-block">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-700">
                <ShieldCheck className="w-3.5 h-3.5" /> SSL details
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="beacon-label">Issuer</label>
                  <input className="beacon-input" placeholder="e.g. Sectigo, DigiCert, Let's Encrypt"
                    data-testid="sub-ssl-issuer-input"
                    value={form.ssl_issuer} onChange={upd("ssl_issuer")} />
                </div>
                <div>
                  <label className="beacon-label">Validity (years)</label>
                  <select className="beacon-input" data-testid="sub-ssl-validity-select"
                    value={form.ssl_validity_years} onChange={upd("ssl_validity_years")}>
                    {YEAR_OPTIONS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="beacon-label">Renewed for (years)</label>
                  <select className="beacon-input" data-testid="sub-ssl-renewed-select"
                    value={form.ssl_renewed_for_years} onChange={upd("ssl_renewed_for_years")}>
                    <option value="">— none —</option>
                    {YEAR_OPTIONS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
          {isServer && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-4" data-testid="server-fields-block">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-700">
                <Server className="w-3.5 h-3.5" /> Server details
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="beacon-label">Type</label>
                  <select className="beacon-input" data-testid="sub-server-type-select"
                    value={form.server_type} onChange={upd("server_type")}>
                    {SERVER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="beacon-label">Status</label>
                  <select className="beacon-input" data-testid="sub-server-status-select"
                    value={form.server_status} onChange={upd("server_status")}>
                    {SERVER_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="beacon-label">Vendor</label>
                  <select className="beacon-input" data-testid="sub-server-vendor-select"
                    value={form.server_vendor} onChange={upd("server_vendor")}>
                    {SERVER_VENDORS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="beacon-label">Old IP</label>
                  <input className="beacon-input font-mono" placeholder="e.g. 192.0.2.10"
                    data-testid="sub-server-old-ip-input"
                    value={form.server_old_ip} onChange={upd("server_old_ip")} />
                </div>
                <div>
                  <label className="beacon-label">New IP</label>
                  <input className="beacon-input font-mono" placeholder="e.g. 198.51.100.20"
                    data-testid="sub-server-new-ip-input"
                    value={form.server_new_ip} onChange={upd("server_new_ip")} />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="beacon-label">Host name</label>
                  <input className="beacon-input" placeholder="e.g. web01.company.com"
                    data-testid="sub-server-hostname-input"
                    value={form.server_hostname} onChange={upd("server_hostname")} />
                </div>
                <div>
                  <label className="beacon-label">Config</label>
                  <input className="beacon-input" placeholder="e.g. 4 vCPU · 8GB RAM · 100GB SSD"
                    data-testid="sub-server-config-input"
                    value={form.server_config} onChange={upd("server_config")} />
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="beacon-label">Notes</label>
            <textarea rows={2} className="beacon-input resize-y" data-testid="sub-notes-input"
              value={form.notes} onChange={upd("notes")} />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="sub-save-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Saving..." : (isEdit ? "Save changes" : "Add subscription")}
          </Button>
        </div>
      </form>
    </div>
  );
}

const SERVICE_LABELS = { hosting: "Hosting", email: "Email", ssl: "SSL", server: "Server" };

function BulkInvoiceModal({ open, onClose, onSaved }) {
  const [step, setStep] = useState("pick"); // "pick" → choose customer, "edit" → set fields + save
  const [names, setNames] = useState([]);
  const [q, setQ] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [subs, setSubs] = useState([]);
  const [checked, setChecked] = useState({});
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(""); // dd/mm/yyyy
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("pick"); setQ(""); setSelectedName(""); setSubs([]); setChecked({}); setInvoiceNo(""); setInvoiceDate("");
    (async () => {
      setLoading(true);
      try {
        // Distinct customer names sourced from all subscriptions
        const r = await api.get("/subscriptions?q=");
        const distinct = new Map();
        for (const s of (r.data || [])) {
          const key = (s.customer_name || "").trim();
          if (!key) continue;
          const k = key.toLowerCase();
          if (!distinct.has(k)) distinct.set(k, { name: key, count: 0 });
          distinct.get(k).count += 1;
        }
        setNames(Array.from(distinct.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        toast.error("Could not load customers", { description: formatApiError(err) });
      } finally { setLoading(false); }
    })();
  }, [open]);

  const pickCustomer = async (name) => {
    setSelectedName(name);
    setLoading(true);
    try {
      const r = await api.get(`/subscriptions?q=${encodeURIComponent(name)}`);
      const nameNorm = name.trim().toLowerCase();
      const rows = (r.data || []).filter((s) => (s.customer_name || "").trim().toLowerCase() === nameNorm);
      setSubs(rows);
      const initial = {};
      for (const s of rows) initial[s.id] = true;
      setChecked(initial);
      // Prefill from any row that already has invoice fields
      const withInv = rows.find((s) => s.last_invoice_number);
      if (withInv) setInvoiceNo(withInv.last_invoice_number || "");
      const withDate = rows.find((s) => s.last_invoice_date);
      if (withDate) setInvoiceDate(toDMY(withDate.last_invoice_date));
      setStep("edit");
    } catch (err) {
      toast.error("Could not load subscriptions", { description: formatApiError(err) });
    } finally { setLoading(false); }
  };

  const toggleAll = (v) => {
    const next = {};
    for (const s of subs) next[s.id] = v;
    setChecked(next);
  };

  const selectedCount = Object.values(checked).filter(Boolean).length;

  const save = async () => {
    const ids = Object.entries(checked).filter(([_, v]) => v).map(([id]) => id);
    if (ids.length === 0) {
      toast.error("Select at least one subscription to update.");
      return;
    }
    const hasNo = invoiceNo.trim() !== "";
    const hasDate = invoiceDate.trim() !== "";
    if (!hasNo && !hasDate) {
      toast.error("Enter a new invoice # or a new date (or both).");
      return;
    }
    if (hasDate && !isValidDMY(invoiceDate)) {
      toast.error("Enter the date as dd/mm/yyyy.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: selectedName,
        subscription_ids: ids,
      };
      if (hasNo) payload.last_invoice_number = invoiceNo.trim();
      if (hasDate) payload.last_invoice_date = dmyToISO(invoiceDate);
      const { data } = await api.post("/subscriptions/bulk-update-invoice", payload);
      toast.success(`Updated ${data.modified} of ${data.matched} subscription${data.matched === 1 ? "" : "s"}`);
      onSaved();
    } catch (err) {
      toast.error("Bulk update failed", { description: formatApiError(err) });
    } finally { setSaving(false); }
  };

  if (!open) return null;

  const filteredNames = q.trim()
    ? names.filter((n) => n.name.toLowerCase().includes(q.trim().toLowerCase()))
    : names;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="bulk-invoice-modal"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-600" />
              Bulk update — Last invoice
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {step === "pick"
                ? "Choose a customer to bulk-edit every service billed together."
                : `Customer: ${selectedName} · ${subs.length} service${subs.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"
            data-testid="bulk-invoice-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === "pick" && (
          <div className="p-5 flex-1 overflow-y-auto space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="beacon-input pl-9" placeholder="Search customer name…"
                value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="bulk-customer-search" autoFocus />
            </div>
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500">Loading customers…</div>
            ) : filteredNames.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No customers found.</div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                {filteredNames.map((n) => (
                  <button
                    key={n.name}
                    onClick={() => pickCustomer(n.name)}
                    data-testid={`bulk-customer-pick-${n.name}`}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
                  >
                    <span className="font-semibold text-slate-900 truncate">{n.name}</span>
                    <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500 shrink-0">
                      {n.count} {n.count === 1 ? "service" : "services"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === "edit" && (
          <div className="p-5 flex-1 overflow-y-auto space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="beacon-label">New invoice # (optional)</label>
                <input className="beacon-input font-mono" placeholder="e.g. INV-2026-001"
                  data-testid="bulk-invoice-no-input"
                  value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
              </div>
              <div>
                <label className="beacon-label">New invoice date (optional, dd/mm/yyyy)</label>
                <input
                  type="text" inputMode="numeric" placeholder="dd/mm/yyyy" maxLength={10}
                  className={`beacon-input font-mono ${invoiceDate && !isValidDMY(invoiceDate) ? "border-rose-400 focus:border-rose-500" : ""}`}
                  data-testid="bulk-invoice-date-input"
                  value={invoiceDate}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
                    let out = digits;
                    if (digits.length > 4) out = `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
                    else if (digits.length > 2) out = `${digits.slice(0,2)}/${digits.slice(2)}`;
                    setInvoiceDate(out);
                  }}
                />
                {invoiceDate && !isValidDMY(invoiceDate) && (
                  <div className="text-[11px] text-rose-600 mt-1">Enter a valid date as dd/mm/yyyy.</div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-600">
                <span className="font-semibold text-slate-900">{selectedCount}</span> of {subs.length} selected
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => toggleAll(true)}
                  data-testid="bulk-select-all"
                  className="text-[11px] font-semibold text-blue-700 hover:underline">Select all</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => toggleAll(false)}
                  data-testid="bulk-clear-all"
                  className="text-[11px] font-semibold text-slate-500 hover:underline">Clear</button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="grid grid-cols-[auto_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                <span></span>
                <span>Service</span>
                <span>Domain / Host</span>
                <span>Current invoice #</span>
                <span>Current invoice date</span>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                {subs.map((s) => {
                  const label = SERVICE_LABELS[s.service_type] || s.service_type;
                  const dh = s.service_type === "server"
                    ? (s.server_hostname || "—")
                    : (s.domain || "—");
                  return (
                    <label key={s.id}
                      data-testid={`bulk-sub-row-${s.id}`}
                      className="grid grid-cols-[auto_1fr_1fr_1fr_1fr] gap-2 px-3 py-2.5 hover:bg-slate-50 items-center cursor-pointer">
                      <input type="checkbox"
                        checked={!!checked[s.id]}
                        onChange={(e) => setChecked({ ...checked, [s.id]: e.target.checked })}
                        data-testid={`bulk-sub-check-${s.id}`}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm font-semibold text-slate-800">{label}</span>
                      <span className="text-sm text-slate-700 truncate">{dh}</span>
                      <span className="text-sm font-mono text-slate-700 truncate">{s.last_invoice_number || "—"}</span>
                      <span className="text-sm text-slate-700">{s.last_invoice_date ? toDMY(s.last_invoice_date) : "—"}</span>
                    </label>
                  );
                })}
                {subs.length === 0 && (
                  <div className="p-6 text-center text-sm text-slate-500">No subscriptions for this customer.</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="p-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2">
          {step === "edit" ? (
            <Button variant="ghost" onClick={() => setStep("pick")} data-testid="bulk-back-btn">
              ← Change customer
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            {step === "edit" && (
              <Button
                onClick={save}
                disabled={saving || selectedCount === 0}
                data-testid="bulk-save-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {saving ? "Updating..." : `Update ${selectedCount} ${selectedCount === 1 ? "service" : "services"}`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Subscriptions() {
  const { user } = useAuth();
  const location = useLocation();
  const [activeService, setActiveService] = useState("hosting");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [globalResults, setGlobalResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [serviceCounts, setServiceCounts] = useState({});

  const loadCounts = useCallback(async () => {
    try {
      const r = await api.get("/subscriptions/counts");
      setServiceCounts(r.data || {});
    } catch (err) {
      // non-fatal
      console.warn("Could not load subscription counts:", err);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const r = await api.get("/subscriptions/customers");
      setCustomers(r.data);
    } catch (err) {
      // non-fatal: autocomplete just stays empty
      console.warn("Could not load subscription customers for autocomplete:", err);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listRes = await api.get(`/subscriptions?service_type=${activeService}`);
      setItems(listRes.data);
    } catch (err) {
      toast.error("Could not load subscriptions", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [activeService]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Deep-link support from Upcoming Renewals — expand & scroll to a company.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const svc = params.get("service");
    const company = (params.get("company") || "").trim();
    if (svc && SERVICES.some((s) => s.key === svc)) {
      setActiveService(svc);
    }
    if (company) {
      const key = company.toLowerCase();
      setExpanded((prev) => ({ ...prev, [key]: true }));
      // Scroll after list has loaded/rendered
      const timer = setTimeout(() => {
        const el = document.querySelector(`[data-testid="customer-group-${key}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setHighlightId(`group:${key}`);
      }, 300);
      const clear = setTimeout(() => setHighlightId(null), 2500);
      return () => { clearTimeout(timer); clearTimeout(clear); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, loading]);

  // Global search across all services (debounced)
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setGlobalResults([]); setSearchOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/subscriptions?q=${encodeURIComponent(term)}`);
        setGlobalResults(r.data);
        setSearchOpen(true);
      } catch (err) {
        // non-fatal: dropdown just stays closed
        console.warn("Global subscription search failed:", err);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const jumpTo = (s) => {
    setActiveService(s.service_type);
    const gk = (s.customer_name || "").trim().toLowerCase();
    setExpanded((prev) => ({ ...prev, [gk]: true }));
    setSearchOpen(false);
    setQ("");
    setHighlightId(s.id);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="subscription-row-${s.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    setTimeout(() => setHighlightId(null), 2200);
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((x) =>
      [x.customer_name, x.customer_email, x.customer_phone, x.domain, x.last_invoice_number,
       x.notes, x.ssl_issuer, x.server_hostname, x.server_old_ip, x.server_new_ip, x.server_config]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [items, q]);

  // Group strictly by customer name (email may repeat across different companies)
  const grouped = useMemo(() => {
    const g = new Map();
    for (const s of filtered) {
      const key = (s.customer_name || "").trim().toLowerCase() || "—";
      if (!g.has(key)) {
        g.set(key, {
          key,
          customer_name: s.customer_name,
          emails: new Set(),
          phones: new Set(),
          items: [],
        });
      }
      const bucket = g.get(key);
      if (s.customer_email) bucket.emails.add(s.customer_email);
      if (s.customer_phone) bucket.phones.add(s.customer_phone);
      bucket.items.push(s);
    }
    return Array.from(g.values())
      .map((b) => ({ ...b, emails: Array.from(b.emails), phones: Array.from(b.phones) }))
      .sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""));
  }, [filtered]);

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (s) => { setEditing(s); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.service_type} subscription for ${s.customer_name}?`)) return;
    try {
      await api.delete(`/subscriptions/${s.id}`);
      toast.success("Removed");
      load();
      loadCustomers();
      loadCounts();
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const sendReminder = async (s) => {
    if (!window.confirm(`Send a renewal reminder email to ${s.customer_email}?`)) return;
    try {
      await api.post(`/subscriptions/${s.id}/reminder`);
      toast.success(`Reminder sent to ${s.customer_email}`);
    } catch (err) {
      toast.error("Could not send reminder", { description: formatApiError(err) });
    }
  };

  const isAdmin = user.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            Subscriptions
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Track customer services and upcoming renewals.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-invoice-btn"
              variant="outline"
              className="font-semibold rounded-lg h-10 border-slate-300 text-slate-800 hover:bg-slate-50"
            >
              <Receipt className="w-4 h-4 mr-1.5" /> Bulk update invoice
            </Button>
            <Button onClick={openAdd} data-testid="add-subscription-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg h-10">
              <Plus className="w-4 h-4 mr-1.5" /> Add subscription
            </Button>
          </div>
        )}
      </div>

      {/* Upcoming dashboard — collapsible (shared component, also embedded on the workspace) */}
      <UpcomingRenewalsCard />

      {/* Service tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {SERVICES.map((s) => {
          const Icon = s.icon;
          const active = activeService === s.key;
          const count = serviceCounts[s.key];
          return (
            <button
              key={s.key}
              disabled={!s.enabled}
              onClick={() => s.enabled && setActiveService(s.key)}
              data-testid={`service-tab-${s.key}`}
              className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all whitespace-nowrap ${
                active
                  ? "bg-slate-900 text-white shadow-sm"
                  : s.enabled
                  ? "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                  : "bg-slate-50 border border-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              <Icon className="w-4 h-4" />
              {s.label}
              {s.enabled && typeof count === "number" && (
                <span
                  className={`text-xs font-mono ${
                    active ? "text-white/80" : "text-slate-500"
                  }`}
                  data-testid={`service-tab-${s.key}-count`}
                >
                  ({count})
                </span>
              )}
              {!s.enabled && <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400 bg-slate-100 rounded px-1 py-0.5">soon</span>}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="beacon-card p-3 relative">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="beacon-input pl-9" placeholder="Search across services — name, email, phone, domain, invoice #, hostname, IP, issuer…"
            data-testid="subscriptions-search"
            value={q}
            onFocus={() => { if (globalResults.length) setSearchOpen(true); }}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)} />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setSearchOpen(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              data-testid="subscriptions-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchOpen && q.trim().length >= 2 && (
          <div className="absolute left-3 right-3 top-full mt-2 z-30 bg-white rounded-xl border border-slate-200 shadow-xl max-h-96 overflow-y-auto"
               data-testid="global-search-results">
            {globalResults.length === 0 ? (
              <div className="p-4 text-sm text-slate-500 text-center">No matches across any service.</div>
            ) : (
              <>
                <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-100 flex items-center justify-between">
                  <span>{globalResults.length} match{globalResults.length === 1 ? "" : "es"} across services</span>
                  <button onClick={() => setSearchOpen(false)} className="text-slate-400 hover:text-slate-700" aria-label="Close">
                    <X className="w-3 h-3" />
                  </button>
                </div>
                <ul className="divide-y divide-slate-100">
                  {globalResults.slice(0, 20).map((r) => {
                    const svc = SERVICES.find((x) => x.key === r.service_type);
                    const Icon = svc?.icon || Globe;
                    const detail = r.service_type === "server"
                      ? (r.server_hostname || r.server_new_ip || r.server_old_ip || "—")
                      : (r.domain || r.customer_email);
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          onClick={() => jumpTo(r)}
                          data-testid={`search-result-${r.id}`}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
                        >
                          <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 grid place-items-center shrink-0">
                            <Icon className="w-3.5 h-3.5 text-slate-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{svc?.label || r.service_type}</span>
                              {r.server_status && (
                                <span className={`beacon-badge ${r.server_status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                                  {r.server_status}
                                </span>
                              )}
                            </div>
                            <div className="font-semibold text-sm text-slate-900 truncate">{r.customer_name}</div>
                            <div className="text-xs text-slate-500 truncate font-mono">{detail}</div>
                          </div>
                          <span className="text-[11px] text-blue-700 font-semibold shrink-0">Open →</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      {/* Grouped table */}
      <div className="beacon-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading subscriptions...</div>
        ) : grouped.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-sm text-slate-500">No {activeService} subscriptions yet.</div>
            {isAdmin && (
              <Button onClick={openAdd} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Add the first one
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {grouped.map((g) => {
              const many = g.items.length > 1;
              const open = expanded[g.key] ?? many; // multi-service defaults open
              const emailLine = g.emails.length === 0
                ? ""
                : g.emails.length === 1
                  ? g.emails[0]
                  : `${g.emails[0]} +${g.emails.length - 1} more`;
              const phoneLine = g.phones.length === 0
                ? ""
                : g.phones.length === 1
                  ? g.phones[0]
                  : `${g.phones[0]} +${g.phones.length - 1} more`;
              return (
                <div key={g.key} data-testid={`customer-group-${g.key}`}>
                  <button
                    onClick={() => setExpanded({ ...expanded, [g.key]: !open })}
                    className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 bg-slate-50/40 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`customer-toggle-${g.key}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{g.customer_name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {emailLine && <span className="font-mono">{emailLine}</span>}
                          {emailLine && phoneLine && <> · </>}
                          {phoneLine && <span>{phoneLine}</span>}
                        </div>
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-500 shrink-0">
                      {g.items.length} {g.items.length === 1 ? "service" : "services"}
                    </span>
                  </button>
                  {open && (
                    <div className="bg-white">
                      {g.items.map((s) => {
                        const isEmailRow = s.service_type === "email";
                        const isSslRow = s.service_type === "ssl";
                        const isServerRow = s.service_type === "server";
                        const emailTypeLabel = s.email_type === "google_workspace" ? "Google Workspace" : s.email_type === "business_email" ? "Business Email" : null;
                        const serverTypeLabel = SERVER_TYPES.find((t) => t.value === s.server_type)?.label;
                        const serverVendorLabel = SERVER_VENDORS.find((v) => v.value === s.server_vendor)?.label;
                        const serverStatusActive = s.server_status === "active";
                        return (
                        <div key={s.id} className={`px-4 sm:px-5 py-3 border-t border-slate-100 flex items-center gap-3 flex-wrap transition-shadow ${highlightId === s.id ? "bg-amber-50 ring-2 ring-amber-300 rounded-md" : ""}`}
                          data-testid={`subscription-row-${s.id}`}>
                          <div className="min-w-0 flex-1 grid sm:grid-cols-4 gap-3 items-center">
                            <div>
                              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                                {isEmailRow ? "Domain · Plan" : isSslRow ? "Domain · Issuer" : isServerRow ? "Host · Vendor" : "Domain"}
                              </div>
                              <div className="text-sm font-semibold text-slate-800 truncate">
                                {isServerRow ? (s.server_hostname || s.domain || "—") : (s.domain || "—")}
                              </div>
                              {isEmailRow && emailTypeLabel && (
                                <span className="beacon-badge bg-blue-50 text-blue-700 border-blue-200 mt-1" data-testid={`sub-email-type-${s.id}`}>
                                  {emailTypeLabel}
                                </span>
                              )}
                              {isSslRow && s.ssl_issuer && (
                                <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200 mt-1" data-testid={`sub-ssl-issuer-${s.id}`}>
                                  {s.ssl_issuer}
                                </span>
                              )}
                              {isServerRow && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {serverTypeLabel && (
                                    <span className="beacon-badge bg-slate-100 text-slate-700 border-slate-200" data-testid={`sub-server-type-${s.id}`}>
                                      {serverTypeLabel}
                                    </span>
                                  )}
                                  {serverVendorLabel && (
                                    <span className="beacon-badge bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`sub-server-vendor-${s.id}`}>
                                      {serverVendorLabel}
                                    </span>
                                  )}
                                  {s.server_status && (
                                    <span className={`beacon-badge ${serverStatusActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid={`sub-server-status-${s.id}`}>
                                      <span className={`beacon-dot ${serverStatusActive ? "bg-emerald-500" : "bg-rose-500"}`} /> {serverStatusActive ? "Active" : "Terminated"}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {isEmailRow ? (
                              <div data-testid={`sub-users-cell-${s.id}`}>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Users</div>
                                <div className="text-sm text-slate-700">
                                  <span className="font-semibold text-slate-900">{s.total_users ?? 0}</span>
                                  <span className="text-slate-400"> total</span>
                                  {s.pending_users != null && s.pending_users > 0 && (
                                    <> · <span className="font-semibold text-amber-700">{s.pending_users}</span> <span className="text-slate-400">pending</span></>
                                  )}
                                </div>
                              </div>
                            ) : isServerRow ? (
                              <div data-testid={`sub-server-ip-cell-${s.id}`}>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">IP · Old → New</div>
                                <div className="text-sm font-mono text-slate-700 truncate">
                                  {s.server_old_ip || "—"} <span className="text-slate-400">→</span> {s.server_new_ip || "—"}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Invoice #</div>
                                <div className="text-sm font-mono text-slate-700 truncate">{s.last_invoice_number || "—"}</div>
                              </div>
                            )}
                            {isSslRow ? (
                              <div data-testid={`sub-ssl-term-cell-${s.id}`}>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Validity · last invoice</div>
                                <div className="text-sm text-slate-700">
                                  <span className="font-semibold text-slate-900">{s.ssl_validity_years || "—"}</span>
                                  <span className="text-slate-400"> yrs</span>
                                  {s.ssl_renewed_for_years ? (
                                    <> · <span className="font-semibold text-emerald-700">renewed {s.ssl_renewed_for_years}</span> <span className="text-slate-400">yrs</span></>
                                  ) : null}
                                  <div className="text-xs text-slate-400 mt-0.5">{formatDate(s.last_invoice_date)}</div>
                                </div>
                              </div>
                            ) : isServerRow ? (
                              <div data-testid={`sub-server-config-cell-${s.id}`}>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Config · Cycle</div>
                                <div className="text-sm text-slate-700 truncate" title={s.server_config || ""}>
                                  {s.server_config || "—"}
                                </div>
                                <div className="text-xs text-slate-400 mt-0.5">
                                  {DURATIONS.find((d) => d.value === s.duration)?.label || s.duration} · {formatDate(s.last_invoice_date)}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Cycle · last invoice</div>
                                <div className="text-sm text-slate-700">
                                  {DURATIONS.find((d) => d.value === s.duration)?.label || s.duration}
                                  <span className="text-slate-400"> · {formatDate(s.last_invoice_date)}</span>
                                </div>
                              </div>
                            )}
                            <div>
                              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Renews</div>
                              <div className="text-sm text-slate-700 flex items-center gap-2">
                                {formatDate(s.renewal_date)}
                                <RenewalBadge days={s.days_until_renewal} />
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => sendReminder(s)} data-testid={`sub-remind-${s.id}`}
                              className="text-blue-700 hover:text-blue-800 hover:bg-blue-50">
                              <Bell className="w-3.5 h-3.5" />
                            </Button>
                            {isAdmin && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`sub-edit-${s.id}`}>
                                  <Edit3 className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`sub-delete-${s.id}`}
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );})}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SubscriptionModal
        open={modalOpen}
        initial={editing}
        defaultService={activeService}
        customers={customers}
        onClose={closeModal}
        onSaved={() => { closeModal(); load(); loadCustomers(); loadCounts(); }}
      />

      <BulkInvoiceModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={() => { setBulkOpen(false); load(); loadCustomers(); loadCounts(); }}
      />
    </div>
  );
}
